// ── Auth Flow (used by infrastructure: cognito/index.ts, validate.ts) ────────

export enum AuthFlow {
  ProceedWithLogin = 'Auth.Flow.ProceedWithLogin',
  RequiresPasswordReset = 'Auth.Flow.RequiresPasswordReset',
  RequiresEmailVerification = 'Auth.Flow.RequiresEmailVerification'
}

// ── Sign-up (used by infrastructure: sign-up.ts, client-app) ─────────────────

export enum SignUpStatus {
  Confirmed = 'Auth.SignUp.Confirmed',
  RequiresEmailVerification = 'Auth.SignUp.RequiresEmailVerification',
  Incomplete = 'Auth.SignUp.Incomplete'
}

export enum SignUpStep {
  Attempt = 'Auth.SignUp.Attempt',
  Confirm = 'Auth.SignUp.Confirm',
  ResendCodeError = 'Auth.SignUp.ResendCodeError'
}

// ── IDP (used by infrastructure: amplify/provider.ts, identity-provider.ts) ──

export enum IdpSignInStep {
  SignInAttempt = 'Auth.Idp.SignInAttempt',
  SignInComplete = 'Auth.Idp.SignInComplete',
  RequiresEmailVerification = 'Auth.Idp.RequiresEmailVerification',
  RequiresNewPassword = 'Auth.Idp.RequiresNewPassword',
  ClearStaleSessionError = 'Auth.Idp.ClearStaleSessionError'
}

export enum IdpEvent {
  GetSignInProvider = 'Auth.Idp.GetSignInProvider',
  GetSignInProviderNoSession = 'Auth.Idp.GetSignInProviderNoSession'
}

// ── Probe (used by infrastructure: amplify/provider.ts, cognito/index.ts) ────

export enum ProbeStatus {
  AccountReady = 'Auth.Probe.AccountReady',
  RequiresPasswordReset = 'Auth.Probe.RequiresPasswordReset',
  RequiresEmailVerification = 'Auth.Probe.RequiresEmailVerification',
  UserNotFound = 'Auth.Probe.UserNotFound',
  Unknown = 'Auth.Probe.Unknown'
}

// ── Social Flow (used by infrastructure: social.ts) ──────────────────────────

export enum SocialFlowStatus {
  AwaitingStart = 'Auth.Social.AwaitingStart',
  ClearingSession = 'Auth.Social.ClearingSession',
  RedirectingToProvider = 'Auth.Social.RedirectingToProvider',
  ExchangingToken = 'Auth.Social.ExchangingToken',
  Authenticated = 'Auth.Social.Authenticated',
  CollectingPhone = 'Auth.Social.CollectingPhone',
  Failed = 'Auth.Social.Failed',
  Hydrated = 'Auth.Social.Hydrated',
  Transition = 'Auth.Social.Transition',
  Ignored = 'Auth.Social.Ignored',
  MalformedPendingState = 'Auth.Social.MalformedPendingState',
  Error = 'Auth.Social.Error',
  ClearStaleSessionError = 'Auth.Social.ClearStaleSessionError',
  ProfileForwarded = 'Auth.Social.ProfileForwarded',
  PrivateEmailExposed = 'Auth.Social.PrivateEmailExposed',
  AuthenticateAfterRedirect = 'Auth.Social.AuthenticateAfterRedirect',
  StaleSessionRetry = 'Auth.Social.StaleSessionRetry',
  PopupBlocked = 'Auth.Social.PopupBlocked',
  PopupClosedByUser = 'Auth.Social.PopupClosedByUser',
}

// ── Token Exchange (used by infrastructure: token-exchange.ts) ────────────────

export enum TokenExchangeStep {
  Request = 'Auth.TokenExchange.Request',
  Success = 'Auth.TokenExchange.Success',
  RegistrationRequired = 'Auth.TokenExchange.RegistrationRequired',
  Error = 'Auth.TokenExchange.Error',
  SubmitPhoneRequest = 'Auth.TokenExchange.SubmitPhoneRequest',
  SubmitPhoneSuccess = 'Auth.TokenExchange.SubmitPhoneSuccess'
}

// ── Session (used by infrastructure: session.ts) ─────────────────────────────

export enum SessionStep {
  Refresh = 'Auth.Session.Refresh',
  RefreshNoSession = 'Auth.Session.RefreshNoSession',
  SignOut = 'Auth.Session.SignOut',
  SignOutError = 'Auth.Session.SignOutError',
  RevalidateTimeout = 'Auth.Session.RevalidateTimeout',
  SubmitPhone = 'Auth.Session.SubmitPhone',
  Start = 'Auth.Session.Start',
  NeedsPhone = 'Auth.Session.NeedsPhone',
  Refreshed = 'Auth.Session.Refreshed',
  Expired = 'Auth.Session.Expired',
  FailOpen = 'Auth.Session.FailOpen',
  ExpiredOnError = 'Auth.Session.ExpiredOnError'
}

// ── Delete Account (used by infrastructure: delete-account.ts) ───────────────

export enum DeleteAccountStep {
  Request = 'Auth.DeleteAccount.Request',
  Success = 'Auth.DeleteAccount.Success'
}

// ── Factory (used by infrastructure: factory.ts) ─────────────────────────────

export enum FactoryEvent {
  Assembled = 'Auth.Factory.Assembled'
}

// ── Auth Outcome (used by domain/types/results.ts and legacy form components) ─

export enum AuthOutcome {
  RequiresEmailVerification = 'Auth.Outcome.RequiresEmailVerification',
  RequiresPasswordReset = 'Auth.Outcome.RequiresPasswordReset',
  RequiresPhone = 'Auth.Outcome.RequiresPhone'
}

// ── Domain Errors (used by infrastructure: errors.ts, sign-in.ts) ────────────

export enum DomainErrorName {
  UserNotConfirmed = 'Auth.DomainError.UserNotConfirmed',
  PasswordResetRequired = 'Auth.DomainError.PasswordResetRequired',
  PrivateEmailExposed = 'Auth.DomainError.PrivateEmailExposed'
}

export enum AuthErrorCode {
  PrivateEmailExposed = 'Auth.Error.PrivateEmailExposed',
  SocialSignInFailed = 'Auth.Error.SocialSignInFailed'
}

// ── Observability (values are NewRelic event names — do not change) ───────────

export enum ObservabilityEvent {
  RegisterFailed = 'auth_register_failure',
  RegisterSucceeded = 'auth_register_success',
  SignInFailed = 'auth_signin_failure',
  SignInSucceeded = 'auth_signin_success',
  SocialSignInSucceeded = 'auth_social_signin_success',
  ChangePasswordSucceeded = 'auth_change_password_success',
  SubmitPhoneSucceeded = 'auth_submit_phone_success',
  SessionRefreshSucceeded = 'auth_session_refresh_success',
  DeleteAccountSucceeded = 'auth_delete_account_success',
  EmailLookupFailed = 'auth_email_lookup_failure',
  IdpProbeFailed = 'auth_idp_probe_failure',
  SessionFailed = 'auth_session_failure',
  DeleteAccountFailed = 'auth_delete_account_failure',
  SignOutFailed = 'auth_signout_failure',
  SocialSignInFailed = 'auth_social_signin_failure',
  ResendCodeFailed = 'auth_resend_code_failure',
  ClearStaleSessionFailed = 'auth_clear_stale_session_failure',
  ChangePasswordFailed = 'auth_change_password_failure',
  SubmitPhoneFailed = 'auth_submit_phone_failure',
  BootstrapFailed = 'auth_bootstrap_failure',
  UpdateProfileFailed = 'auth_update_profile_failure',
  UpdateEmailFailed = 'auth_update_email_failure',
  UpdateEmailConfirmationFailed = 'auth_update_email_confirmation_failure',
}

export enum ErrorCategory {
  InvalidCredentials = 'invalid_credentials',
  ValidationRejected = 'validation_error',
  ApiCallFailed = 'api_error',
  UnknownError = 'unknown_error'
}

export enum Severity {
  Critical = 'critical'
}

// ── Locale Keys (values are i18n lookup keys — do not change) ────────────────

export enum LocaleKey {
  InvalidPassword = 'formError:invalid_password',
  InvalidPasswordFormat = 'formError:invalid_password_format',
  InvalidEmail = 'formError:invalid_email',
  InvalidCode = 'formError:invalid_code',
  InvalidPhone = 'formError:invalid_phone',
  ExpiredCode = 'formError:expired_code',
  EmailTaken = 'formError:email_taken',
  Other = 'formError:other',
  PasswordChangedSuccess = 'formError:password_changed_success',
  PasswordResetEmailSent = 'formError:password_reset_email_sent',
  ResetTokenInvalid = 'formError:reset_token_invalid',
  InfoChangedSuccess = 'formError:info_changed_success',
  ValidationRequestFailed = 'formError:validation_request_failed',
  ValidationErrorMessage = 'formError:validation_error_message',
  AccountConfirmationFailed = 'formError:account_confirmation_failed',
  AccountDeleteFailed = 'formError:account_delete_failed',
  AccountDeletedSuccess = 'formError:account_deleted_success',
  PrivateEmailExposed = 'formError:private_email_exposed'
}
